import { useEffect, useState } from "react";
import "./waitingRoom.scss";
import StatusScreen from "../../Menus/StatusScreen/StatusScreen";
import { QRCodeCanvas } from "../QRCodeCanvas";
import { JoinGameModal } from "./components/JoinGameModal";
import { isUserOnTV } from "@sinclair-digital/cabin-auth-library";
// import ChessButton from "../../visual/ChessButton/ChessButton";
import useSoundEffect from "../../../hooks/useSoundEffect";
import { KeyCodes } from "../../../utils/navigationHelpers";
import { getRandomName } from "../../../utils/nameGenerator";
import { eventQuitGame } from "../../../utils/gtag";
import { useMultiplayer } from "../../../context/MultiplayerContext";
// import ResponsiveBackground from "../../Menus/StartMenu/ResponsiveBackground";

/**
 * WaitingRoom displays a lobby where players can join a multiplayer game.
 * Hosts see a QR code, and others can join via a modal or mobile.
 * @returns the JSX for the waiting room component.
 */
export default function WaitingRoom() {
    const [isJoinModalOpen, setJoinModalOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    const { multiplayerState, joinGame, handleReturnMainMenu } = useMultiplayer();
    const { playSelectAudio, playClickAudio } = useSoundEffect();

    function useSmallScreen(maxWidth: number) {
        const [isSmallScreen, setSmallScreen] = useState(
            typeof window !== "undefined" ? window.innerWidth <= maxWidth : false,
        );

        useEffect(() => {
            const onResize = () => setSmallScreen(window.innerWidth <= maxWidth);
            window.addEventListener("resize", onResize);
            return () => window.removeEventListener("resize", onResize);
        }, [maxWidth]);

        return isSmallScreen;
    }

    const isSmallScreen = useSmallScreen(999);

    // Generate random name function using API
    const generateRandomName = async (setUsername: (name: string) => void) => {
        if (isGenerating) return;
        setIsGenerating(true);
        const generatedName = await getRandomName();
        setUsername(generatedName);
        setIsGenerating(false);
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                KeyCodes.Ups.includes(event.key) ||
                KeyCodes.Downs.includes(event.key) ||
                KeyCodes.Rights.includes(event.key) ||
                KeyCodes.Lefts.includes(event.key)
            ) {
                playClickAudio();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [playClickAudio]);

    /**
     * Handles clicking on the cancel button in the waiting room.
     * Deletes the game and closes the WS connection.
     */
    function handleClickBack(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault();
        playSelectAudio();
        handleReturnMainMenu();
        eventQuitGame();
    }

    if (multiplayerState.lobbyId === null) return <StatusScreen />;

    return (
        <div className="waiting-room" data-testid="waiting-room">
            <div className="columns">
                <div className="row">
                    <h1>Join the game!</h1>
                    <p>Scan the QR code with a mobile device to join the game.</p>
                    <div className="dot-elastic"></div>
                    {/* Shared wrapper for players and buttons */}
                    <div className="player-button-stack">
                        <div className="player-wrapper">
                            <div className="player">{multiplayerState.p1.username || "?"}</div>
                            VS
                            <div className="player">{multiplayerState.p2.username || "?"}</div>
                        </div>

                        {isSmallScreen && (
                            <div className="qr-container-waiting">
                                <QRCodeCanvas lobbyId={multiplayerState.lobbyId} id="qr-code" />
                            </div>
                        )}

                        <div className="waiting-room-btn-cont">
                            {!isUserOnTV() && (
                                <button
                                    className="join-game-btn"
                                    onClick={() => {
                                        window.history.replaceState(
                                            null,
                                            "",
                                            `?lobbyId=${multiplayerState.lobbyId}`,
                                        );
                                        window.location.reload();
                                    }}
                                    data-navcol={0}
                                    data-navrow={0}
                                    autoFocus
                                >
                                    <span className="join-game-text">Join Game</span>
                                </button>
                            )}

                            {isJoinModalOpen && (
                                <JoinGameModal
                                    lobbyId={multiplayerState.lobbyId}
                                    onClose={() => setJoinModalOpen(false)}
                                    onJoin={(username) => {
                                        joinGame(multiplayerState.lobbyId, username);
                                    }}
                                    generateRandomName={generateRandomName}
                                    isGenerating={isGenerating}
                                />
                            )}

                            <button
                                id="cancel-button"
                                onClick={(e) => handleClickBack(e)}
                                data-navcol={1}
                                data-navrow={0}
                                autoFocus={isUserOnTV() && true}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
                {!isSmallScreen && (
                    <div className="qr-container-waiting">
                        <QRCodeCanvas lobbyId={multiplayerState.lobbyId} id="qr-code" />
                    </div>
                )}
            </div>
        </div>
    );
}
